// Credit: the /billing page (both wallets, the trial, buying credit, activity), the small
// credit chip in the chat sidebar, and the action card shown wherever something stops
// because the account is locked or out of AI credit.
//
// Two wallets per account, shared by all of the person's workspaces:
//   hosting — awake time and storage (storage even while asleep)
//   AI      — the agent's model use
// A new account starts with a trial: 30 awake minutes and €2 of AI credit.

import { h, Component } from 'preact';
import { getWallet } from '../api.js';
import { Icon } from './icons.js';
import './ui.css';

export const eur = (cents) => '€' + (Number(cents || 0) / 100).toFixed(2);
const eurs = (x) => (x == null ? '—' : '€' + Number(x).toFixed(2));
const when = (iso) => { try { return new Date(iso + (/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? '' : 'Z')).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }); } catch (e) { return iso; } };
const mins = (s) => Math.max(0, Math.ceil((s || 0) / 60));

// ─── what a stop means, and what fixes it ─────────────────────────────────────
// Codes come from the portal (402 + code) or from the agent's error text (LiteLLM).
const ACTIONS = {
  trial_ended: { title: 'Your free trial has ended', pocket: 'hosting',
    text: 'Your workspace is asleep with all your files. Buy hosting credit to wake it and keep going.', cta: 'Buy hosting credit' },
  trial_limit: { title: 'The trial includes one workspace', pocket: 'hosting',
    text: 'Buy hosting credit to create more sandboxes.', cta: 'Buy hosting credit' },
  hosting_credits: { title: 'Your hosting credit is used up', pocket: 'hosting',
    text: 'Your workspaces are asleep and nothing is being charged. Top up to wake them.', cta: 'Top up hosting' },
  ai_credits: { title: 'Your AI credit is used up', pocket: 'ai',
    text: 'The agent needs AI credit to answer. Top up and send your message again.', cta: 'Top up AI credit' },
  ai_model_locked: { title: 'This model needs AI credit', pocket: 'ai',
    text: 'Until you buy AI credit, your workspace can use the default model only. Buying any amount unlocks the rest of the catalogue.', cta: 'Buy AI credit' },
};

/** "scaleway/deepseek-v4-flash-0731" -> "DeepSeek V4 Flash (Scaleway)".
 *  The ids are provider-prefixed on purpose (the router sells who hosts your
 *  tokens), but a billing page should read like prose. */
export function modelName(id) {
  const raw = String(id || '');
  if (!raw) return '';
  const [provider, rest] = raw.includes('/') ? raw.split('/', 2) : ['', raw];
  const pretty = rest.replace(/-/g, ' ').replace(/\b([a-z])/g, (m) => m.toUpperCase());
  const host = provider ? provider.replace(/^stackit$/i, 'STACKIT').replace(/^scaleway$/i, 'Scaleway') : '';
  return host ? `${pretty} (${host})` : pretty;
}

export function classify(text) {
  const low = String(text || '').toLowerCase();
  if (['exceededbudget', 'budget_exceeded', 'over budget', 'budget has been exceeded'].some((m) => low.includes(m))) return 'ai_credits';
  if (['not allowed to access model', 'key not allowed'].some((m) => low.includes(m))) return 'ai_model_locked';
  return null;
}

export function ActionCard({ code, compact }) {
  const a = ACTIONS[code];
  if (!a) return null;
  return h('div', { className: 'action-card' + (compact ? ' compact' : ''), role: 'status' },
    h('span', { className: 'action-icon', 'aria-hidden': 'true' }, a.pocket === 'ai' ? '✦' : '⏻'),
    h('div', { className: 'grow' }, h('b', null, a.title), h('p', null, a.text)),
    h('a', { className: 'btn primary', href: '/billing?buy=' + a.pocket }, a.cta));
  // (the /billing page is where the balance and the way to the portal live)
}

// ─── the sidebar chip ─────────────────────────────────────────────────────────
export class CreditChip extends Component {
  constructor(p) { super(p); this.state = { w: null }; this.timer = null; }
  componentDidMount() { this.load(); this.timer = setInterval(() => { if (!document.hidden) this.load(); }, 60000); }
  componentWillUnmount() { clearInterval(this.timer); }
  componentDidUpdate(prev) { if (prev.stamp !== this.props.stamp) this.load(); }
  async load() {
    try { const w = await getWallet(); this.setState({ w }); if (this.props.onWallet) this.props.onWallet(w); } catch (e) { /* keep the last */ }
  }
  render(_, { w }) {
    if (!w) return null;
    const t = w.trial || {};
    let label, cls = 'credit-chip';
    if (w.lock) { label = w.lock === 'trial_ended' ? 'Trial ended · buy credit' : 'Out of hosting credit'; cls += ' warn'; }
    else if (t.state === 'none') label = `Free trial · ${t.minutes_total} min`;
    else if (t.state === 'active') label = `Trial · ${mins(t.seconds_left)} min left`;
    else {
      // AI is credits, hosting is euros. Two units in one chip is a little awkward, but
      // it is honest: hosting is a cost passed through, AI is a thing you buy.
      const ai = w.ai && w.ai.credits != null ? ` · AI ${w.ai.credits} cr` : '';
      label = `${eur(w.hosting.balance_cents)} hosting${ai}`;
      if (w.hosting.hours_left != null && w.hosting.hours_left < 24) cls += ' warn';
    }
    return h('a', { className: cls, href: '/billing', title: 'Credit and usage' }, h('span', { className: 'coin', 'aria-hidden': 'true' }), label);
  }
}

// ─── the /billing page ────────────────────────────────────────────────────────

export class BillingPage extends Component {
  // What is left here, and why. The chat has to be able to say "you are out of credit" the
  // moment a turn is refused, so it shows the balances, the trial and the locks — the things
  // that explain why the work stopped. It no longer sells anything: buying, invoices and the
  // ledger are account paperwork, and they live with the rest of it on the platform. One till,
  // not two.
  constructor(p) {
    super(p);
    this.state = { w: null, error: '', focus: null };
  }

  componentDidMount() {
    this.setState({ focus: new URLSearchParams(location.search).get('buy') });
    this.load();
  }

  async load() {
    try { this.setState({ w: await getWallet(), error: '' }); }
    catch (e) { this.setState({ error: e.message }); }
  }

  // The platform's credit page. The URL comes from the portal itself (/api/me), so a
  // deployment that moves hosts does not need this rebuilt.
  topUp() {
    const base = this.props.portal || '';
    return base ? `${base}/portal/credit` : null;
  }

  render(_, s) {
    const w = s.w;
    if (s.error && !w) return h('div', { className: 'bill-page' }, h('p', { className: 'note err' }, s.error));
    if (!w) return h('div', { className: 'bill-page' }, h('div', { className: 'skel' }, h('span', null), h('span', null), h('span', { className: 'short' })));
    const t = w.trial || {}, host = w.hosting || {}, ai = w.ai || {};
    const trialActive = t.state === 'active', trialWaiting = t.state === 'none';
    const portal = this.topUp();
    const buy = (pocket) => portal
      ? h('a', { className: 'btn primary', href: `${portal}?buy=${pocket}`, target: '_blank', rel: 'noopener' },
          'Top up on the platform ↗')
      : null;

    return h('div', { className: 'bill-page' },
      h('div', { className: 'bill-head' },
        h('h1', null, 'Credit'),
        h('p', { className: 'muted' }, 'Two balances, shared by all your workspaces: hosting pays for awake time and storage, AI pays for the agent. You only pay for what you use.')),
      s.error ? h('p', { className: 'note err' }, s.error) : null,
      w.lock ? h(ActionCard, { code: w.lock }) : null,
      w.deletion_at ? h('p', { className: 'banner err', role: 'alert' },
        `Your workspaces and their files will be deleted on ${when(w.deletion_at)} unless you buy hosting credit.`) : null,

      trialWaiting ? h('section', { className: 'bill-card trial' },
        h('div', { className: 'bill-card-head' }, h('h2', null, 'Free trial'), h('span', { className: 'role-badge' }, 'Ready')),
        h('p', null, h('b', null, `${t.minutes_total} minutes of workspace time`),
          `${t.ai_cents ? ' and ' + eur(t.ai_cents) + ' of AI credit' : ''}, on your first workspace.`),
        h('p', { className: 'muted small' }, 'Start a chat and your workspace is created for you — no card needed.'),
        h('a', { className: 'btn primary', href: '/' }, 'Start your first chat')) : null,

      trialActive ? h('section', { className: 'bill-card trial' },
        h('div', { className: 'bill-card-head' }, h('h2', null, 'Free trial'), h('span', { className: 'role-badge' }, 'Active')),
        h('div', { className: 'meter' }, h('span', { style: { width: `${Math.min(100, 100 * (t.seconds_left || 0) / ((t.minutes_total || 30) * 60))}%` } })),
        h('p', null, h('b', null, `${mins(t.seconds_left)} of ${t.minutes_total} minutes left`),
          ` of awake workspace time, plus AI credit on ${modelName(ai.default_model) || 'the default model'}.`),
        h('p', { className: 'muted small' }, 'When the minutes run out your workspace sleeps. Buy hosting credit to wake it; unconverted trial workspaces are deleted after 7 days.')) : null,

      h('div', { className: 'bill-grid' },
        h('section', { className: 'bill-card' + (s.focus === 'hosting' ? ' focus' : '') },
          h('div', { className: 'bill-card-head' }, h('h2', null, 'Hosting'), host.depleted_at ? h('span', { className: 'role-badge warn' }, 'Empty') : null),
          h('div', { className: 'big' }, eur(host.balance_cents)),
          h('p', { className: 'muted small' },
            trialActive ? 'Not charged during the trial.'
              : host.burn_cents_per_hour > 0 ? `Using about ${eur(host.burn_cents_per_hour * 24)} a day right now` + (host.hours_left != null ? ` · roughly ${host.hours_left < 48 ? Math.floor(host.hours_left) + ' hours' : Math.floor(host.hours_left / 24) + ' days'} left.` : '.')
              : 'Nothing is running.'),
          h('p', { className: 'muted small' }, `${eurs(w.rates.awake_hour)} per awake hour · ${eurs(w.rates.storage_gb_month)} per GB of storage a month, asleep or awake.`),
          buy('hosting')),
        h('section', { className: 'bill-card' + (s.focus === 'ai' ? ' focus' : '') },
          h('div', { className: 'bill-card-head' }, h('h2', null, 'AI'),
            ai.all_models ? null : h('span', { className: 'role-badge' }, 'One model')),
          h('div', { className: 'big' }, ai.credits == null ? '—' : `${ai.credits} credits`),
          ai.credits != null ? h('p', { className: 'muted small' },
            `worth ${eurs(ai.credits_eur)} · ${ai.credits_per_eur} credits to the euro`) : null,
          h('p', { className: 'muted small' }, ai.spent_eur == null ? (ai.credited_eur ? 'Usage is updating…' : 'No AI credit yet.') : `${eurs(ai.spent_eur)} used of ${eurs(ai.credited_eur)} so far.`),
          h('p', { className: 'muted small' }, ai.all_models
            ? `All ${(ai.models || []).length || ''} models are available, charged per use.`.replace('  ', ' ')
            : `${modelName(ai.default_model) || 'The default model'} only. Buying AI credit unlocks the whole catalogue.`),
          buy('ai'))),

      portal ? h('p', { className: 'muted small', style: { marginTop: '1rem' } },
        'Top-ups, invoices and your plan live on ',
        h('a', { href: portal, target: '_blank', rel: 'noopener' }, 'the eutonomous platform'),
        ' — along with everything else about your account.') : null);
  }
}
